import React, { useCallback, useMemo } from "react";
import { createUseStyles } from "$utils/createUseStyles";
import { SubmitHandler } from "react-hook-form";
import dateFormat from "dateformat";
import {
  Button,
  InputNumber,
  SelectInput,
  SliderInput,
  Text,
  TextInput,
} from "$components";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import NumberInputWithIncDecBtn from "./NumberInputWithIncDecBtn";
import { theme } from "$utils/theme/bataillog";

export interface ISelectValue {
  text: string;
  value: string;
  disabled?: boolean;
  default?: boolean;
  data?: any;
}

export interface IField {
  type: string;
  name: string;
  className?: string;
  label: string;
  placeholder?: string;
  defaultValue?: string;
  requiredText?: string;
  regex?: string;
  regexText?: string;
  values?: ISelectValue[];
  Child?: React.ReactNode | string;
  disabled?: boolean;
  onSelectAction?: (data?: any) => void;
  multiline?: boolean;
  rowsMax?: number;
  rows?: number;
  id?: string;
  max?: number;
  min?: number;
  handleChangeAction?: (value: any) => void;
  value?: any;
  variant?: string;
  shouldBeDisplayed?: (value: any) => boolean;
}

export interface IformGroup {
  formFields: IField[];
  Child?: React.ReactNode;
  className?: string;
}

export interface IForms {
  className?: string;
  onSubmitAction?: (data: any) => void;
  onCancelAction?: () => void;
  onErrorAction?: (err: any) => void;
  formData?: IformGroup[];
  errorMessage?: string;
  submitLabel?: string;
  contextData?: any;
}

const FormContext = React.createContext<any>({});

function Forms({
  className,
  onSubmitAction,
  onCancelAction,
  onErrorAction,
  errorMessage,
  formData,
  submitLabel,
  contextData,
}: IForms) {
  const classes = useStyles();

  const [values, setValues] = React.useState<any>({});

  const updateValue = (name: string, value: string) => {
    setValues((prev: any) => ({
      ...prev,
      [name]: {
        ...prev,
        value,
      },
    }));
  };

  const open = contextData?.open;

  // get all fields
  const fields: any = {};
  if (formData) {
    formData.forEach((g) => {
      if (g.formFields) {
        g.formFields.forEach((input) => {
          fields[input.name] = input;
        });
      }
    });
  }

  const form = React.useRef<HTMLFormElement | null>(null);
  const [didSubmit, setDidSubmit] = React.useState<boolean>(false);

  // check if data are passed from context
  React.useEffect(() => {
    if (contextData) {
      setValues((prev: any) => ({
        ...prev,
        ...contextData,
      }));
    }
  }, [contextData]);

  // init first value;
  const setFormValues = useCallback(
    (_value?: any) => {
      if (_value && values) {
        setValues((prev: any) => ({
          ...prev,
          ..._value,
        }));
      }
    },
    [values]
  );

  const onSubmit: SubmitHandler<any> = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    let _values: any = values || {};
    let error = false;
    // grab data value
    const data: any = {};
    const _i = form?.current?.querySelectorAll("input,textarea");

    if (_i && _i.length) {
      _i.forEach((i: HTMLInputElement) => {
        if (i?.name) {
          data[i.name] = i.value || "";
          _values = {
            ..._values,
            [i.name]: {
              ..._values[i.name],
              value: data[i.name],
            },
          };
        }
      });
    }

    // test regex for each field
    Object.entries(fields).forEach(([input, value]: [string, any]) => {
      if (value && value.requiredText && data[input] === "") {
        _values = {
          ..._values,
          [input]: {
            ...(_values[input] || {}),
            errorRequired: true,
          },
        };
        error = true;
      }
      // specific for input number with increment/decrement buttons, need to be standardized later
      if (
        value &&
        value.type &&
        value.type === "numberWithIncDecBtn" &&
        data[input] < 1
      ) {
        _values = {
          ..._values,
          [input]: {
            ...(_values[input] || {}),
            errorRequired: true,
          },
        };
        error = true;
      }
      if (
        value &&
        !value.variant &&
        value.checkOnBlurRegex &&
        value.regexText &&
        data[input] !== "" &&
        !new RegExp(value.checkOnBlurRegex).test(data[input])
      ) {
        _values = {
          ..._values,
          [input]: {
            ...(_values[input] || {}),
            errorRegex: true,
          },
        };
        error = true;
      }
      if (
        value &&
        value.variant &&
        value.variant === "date" &&
        value.checkOnBlurRegex &&
        value.regexText &&
        data[input] !== ""
      ) {
        let val = data[input];
        const dt = new Date(val).getTime();
        val = Number.isNaN(dt)
          ? "NaN"
          : dateFormat(new Date(val), DEFAULT_DATE_FORMAT);
        if (!new RegExp(value.checkOnBlurRegex).test(val)) {
          _values = {
            ..._values,
            [input]: {
              ...(_values[input] || {}),
              errorRegex: true,
            },
          };
          error = true;
        }
      }
    });
    setDidSubmit(true);
    if (error) {
      setValues(_values);
      return;
    }
    if (onSubmitAction && data) {
      try {
        onSubmitAction(data);
      } catch (err) {
        if (onErrorAction) {
          onErrorAction(err);
        }
      }
    }
  };

  const onCancel = () => {
    if (onCancelAction) {
      onCancelAction();
    }
  };

  enum FIELD_TYPE {
    TEXT = "text",
    SELECT = "select",
    SLIDER = "slider",
    NUMBER_WITH_INC_DEC_BTN = "numberWithIncDecBtn",
    NUMBER = "number",
  }

  const contextDataValue = useMemo(() => {
    if (contextData)
      return {
        ...contextData,
        didSubmit,
        open,
        values,
        setFormValues,
        updateValue,
      };
    return {
      values,
      setFormValues,
      updateValue,
    };
  }, [contextData, didSubmit, open, setFormValues, values]);

  return (
    <FormContext.Provider value={contextDataValue}>
      <form
        ref={form}
        className={`${classes.GenericForm} ${className ?? ""}`}
        onSubmit={onSubmit}
      >
        {!!formData?.length &&
          formData.map((f, index) => (
            <div key={`formGroup_${index}`}>
              <div className={`${classes.formGroup} ${f.className || ""}`}>
                {!!f.formFields.length &&
                  f.formFields.map((field) => {
                    if (
                      field?.shouldBeDisplayed &&
                      field?.shouldBeDisplayed(values) === false
                    ) {
                      return null;
                    }
                    switch (field.type) {
                      case FIELD_TYPE.TEXT:
                        return (
                          <TextInput
                            value={values[field.name]}
                            errorRequired={values[field.name]?.errorRequired}
                            errorRegex={values[field.name]?.errorRegex}
                            key={field.name}
                            multiline={field.multiline}
                            rows={field.multiline ? 5 : undefined}
                            {...field}
                          />
                        );
                      case FIELD_TYPE.NUMBER:
                        return (
                          <InputNumber
                            value={values[field.name]?.value}
                            errorRequired={values[field.name]?.errorRequired}
                            errorRegex={values[field.name]?.errorRegex}
                            key={field.name}
                            {...field}
                          />
                        );
                      case FIELD_TYPE.NUMBER_WITH_INC_DEC_BTN:
                        return (
                          <NumberInputWithIncDecBtn
                            value={values[field.name]?.value}
                            key={field.name}
                            errorRequired={values[field.name]?.errorRequired}
                            {...field}
                          />
                        );
                      case FIELD_TYPE.SELECT:
                        return (
                          <SelectInput
                            value={values[field.name]}
                            key={field.name}
                            errorRequired={values[field.name]?.errorRequired}
                            {...field}
                          />
                        );
                      case FIELD_TYPE.SLIDER:
                        return (
                          <SliderInput
                            value={values[field.name]?.value}
                            key={field.name}
                            {...field}
                          />
                        );
                      default:
                        return (
                          <TextInput
                            value={values[field.name]}
                            errorRequired={values[field.name]?.errorRequired}
                            errorRegex={values[field.name]?.errorRegex}
                            key={field.name}
                            {...field}
                          />
                        );
                    }
                  })}
              </div>
              {f.Child && f.Child}
            </div>
          ))}
        {errorMessage && errorMessage !== "" && (
          <div className={classes.errorContainer}>{errorMessage}</div>
        )}
        <div className={classes.formGroup}>
          <Button isDefault variant="outlined" onClick={onCancel}>
            Annuler
          </Button>
          <Button className={classes.btnGeneric} type="submit">
            <Text>{submitLabel ?? "Valider"}</Text>
          </Button>
        </div>
      </form>
    </FormContext.Provider>
  );
}

export const useFormContext = () => {
  return React.useContext(FormContext);
};

export default React.memo(Forms);

const useStyles = createUseStyles(() => ({
  GenericForm: {
    width: "100%",
    marginBottom: "58px",
    "& .bordered": {
      display: "flex",
      alignItems: "center",
      borderTop: "1px solid #d8dadc",
      "& h3:not(:first-child)": {
        marginLeft: "10px",
      },
    },
  },
  formGroup: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "inherit",
  },
  btnGeneric: {
    height: "40px",
    "& .MuiButton-label > span": {
      color: theme.color.white,
    },
  },
  errorContainer: {
    color: "#ef7c83",
    marginBottom: 20,
  },
}));
